import React, { useState, useEffect } from 'react';

/**
 * WelcomeStep Component
 * Short welcome message with typing animation
 */
const WelcomeStep = ({ onContinue, onSkipToGifts }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showAdditionalContent, setShowAdditionalContent] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [imageError, setImageError] = useState(false);

  const mainText = "Thank you for your purchase!";
  const typingSpeed = 50; // milliseconds per character

  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index < mainText.length) {
        setDisplayedText(mainText.slice(0, index + 1));
        index++;
      } else {
        clearInterval(timer);
        // Show additional content after typing completes
        setTimeout(() => {
          setShowAdditionalContent(true);
          setTimeout(() => {
            setShowButton(true);
          }, 500);
        }, 300);
      }
    }, typingSpeed);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="max-w-xl w-full">
        {/* Profile Image - Small, top center */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gray-200 border-2 border-gray-300 flex items-center justify-center overflow-hidden">
            {!imageError ? (
              <img
                src="/images/founder.png"
                alt="Sarah, Founder"
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <svg
                className="w-10 h-10 md:w-12 md:h-12 text-gray-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
          </div>
        </div>

        {/* Main typing text */}
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-wide mb-6 md:mb-8 min-h-[100px] md:min-h-[120px] flex items-center justify-center px-4">
            {displayedText}
            {displayedText.length < mainText.length && (
              <span className="animate-pulse ml-1">|</span>
            )}
          </h1>

          {/* Additional content - fades in after typing */}
          <div
            className={`space-y-5 transition-all duration-700 ${
              showAdditionalContent
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
            }`}
          >
            <p className="text-base md:text-lg text-gray-700 leading-relaxed px-4">
              We've prepared some special free gifts for you to make your experience even more memorable.
            </p>

            <p className="text-base md:text-lg text-gray-600 leading-relaxed px-4">
              Your feedback means the world to our small team!
            </p>

            <p className="text-sm md:text-base text-gray-500 italic pt-2">
              – Sarah, Founder
            </p>
          </div>

          {/* Continue button */}
          {showButton && (
            <div className="mt-12 animate-fade-in">
              <button
                onClick={onContinue}
                className="px-12 py-4 bg-black text-white text-lg font-light tracking-wide
                         hover:bg-gray-800 transition-all duration-300
                         focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                Continue
              </button>
            </div>
          )}

          {/* Skip to Gifts - Amazon Compliance */}
          {showButton && (
            <div className="mt-8 pt-8 border-t border-gray-200 animate-fade-in">
              <p className="text-sm text-gray-600 text-center mb-4">
                Prefer to access your gifts directly?
              </p>
              <button
                type="button"
                onClick={onSkipToGifts}
                className="w-full px-8 py-4 border-2 border-green-600 bg-white text-green-700 text-lg font-light tracking-wide
                         hover:bg-green-50 transition-all duration-300
                         focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
              >
                Claim Your Free Gifts
              </button>
              <p className="text-xs text-gray-500 text-center mt-3">
                Access your gifts instantly without sharing your information
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeStep;
